import React, { useState } from 'react';
import { RefreshCw, Loader2, CheckCircle2, AlertTriangle, History } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { cn } from '@/lib/utils';
import WriterVersionHistorySheet from './WriterVersionHistorySheet';

export interface WriterVersionSummary {
  id: string;
  label: string | null;
  compile_status: 'pending' | 'running' | 'success' | 'failed';
  created_at: string;
  artifact_source_key?: string | null;
  /** PDF was compiled with the free-plan PaperVerse credit line. */
  marked?: boolean | null;
  /** When the last compile of this version finished (success or failure). */
  compiled_at?: string | null;
}

interface Props {
  versions: WriterVersionSummary[];
  onRefresh?: () => void;
  onRevert?: (version: WriterVersionSummary) => void | Promise<void>;
  onFork?: (version: WriterVersionSummary) => void | Promise<void>;
  onRetryCompile?: (version: WriterVersionSummary) => void | Promise<void>;
  busyVersionId?: string | null;
}

/**
 * Single-line status bar shown above the compiled PDF pane. Instead of
 * broadcasting every writer_version as a chip (noisy, layout-hostile), it
 * summarises the state that actually matters to the author:
 *
 *   • Up to date    — latest version compiled successfully
 *   • Compiling…    — anything pending or running
 *   • Compile failed — latest failed (the banner beneath surfaces details)
 *
 * Historical navigation (revert / fork / retry older versions) lives in a
 * `History` drawer that opens on demand — keeping the primary surface calm
 * even after dozens of compiles.
 */
const WriterVersionScrubber: React.FC<Props> = ({
  versions,
  onRefresh,
  onRevert,
  onFork,
  onRetryCompile,
  busyVersionId,
}) => {
  const [historyOpen, setHistoryOpen] = useState(false);

  if (!versions.length) return null;

  const latest = versions[0];
  const inFlight = versions.some(
    (v) => v.compile_status === 'pending' || v.compile_status === 'running',
  );

  let icon: React.ReactNode;
  let statusLabel: string;
  let tone: string;
  if (inFlight) {
    icon = <Loader2 className="h-3.5 w-3.5 animate-spin" />;
    statusLabel = 'Compiling…';
    tone = 'text-sky-700 dark:text-sky-300';
  } else if (latest.compile_status === 'failed') {
    icon = <AlertTriangle className="h-3.5 w-3.5" />;
    statusLabel = 'Compile failed';
    tone = 'text-destructive';
  } else if (latest.compile_status === 'success') {
    icon = <CheckCircle2 className="h-3.5 w-3.5" />;
    statusLabel = 'Up to date';
    tone = 'text-emerald-700 dark:text-emerald-300';
  } else {
    icon = <Loader2 className="h-3.5 w-3.5 animate-spin" />;
    statusLabel = 'Compiling…';
    tone = 'text-sky-700 dark:text-sky-300';
  }

  let freshness = '';
  try {
    freshness = formatDistanceToNow(new Date(latest.created_at), { addSuffix: true });
  } catch {
    freshness = '';
  }

  return (
    <>
      <div className="border-b border-border/60 bg-background/70 backdrop-blur px-4 py-2 flex items-center gap-3 min-w-0 max-w-full">
        <span className="text-[10px] uppercase tracking-widest text-muted-foreground shrink-0">
          Draft
        </span>
        <div className={cn('flex items-center gap-1.5 text-sm min-w-0', tone)}>
          {icon}
          <span className="font-medium truncate">{statusLabel}</span>
        </div>
        <span className="text-xs text-muted-foreground truncate min-w-0 hidden sm:inline">
          {latest.label ?? 'Untitled'}
          {freshness ? ` · ${freshness}` : ''}
        </span>
        <div className="ml-auto flex items-center gap-1 shrink-0">
          <button
            type="button"
            onClick={() => setHistoryOpen(true)}
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition rounded-full px-2.5 py-1 hover:bg-muted"
          >
            <History className="h-3.5 w-3.5" />
            History
            <span className="tabular-nums opacity-70">{versions.length}</span>
          </button>
          {onRefresh && (
            <button
              type="button"
              onClick={onRefresh}
              className="text-muted-foreground hover:text-foreground transition rounded-full p-1.5 hover:bg-muted"
              aria-label="Refresh versions"
            >
              <RefreshCw className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>
      <WriterVersionHistorySheet
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        versions={versions}
        onRevert={onRevert}
        onFork={onFork}
        onRetryCompile={onRetryCompile}
        busyVersionId={busyVersionId}
      />
    </>
  );
};

export default WriterVersionScrubber;
